/* The Overview's building blocks: a panel, a KPI tile, a strip, and skeletons shaped like what they
 * stand in for.
 *
 * **Panels, not `Card`, because the Overview has its own surface language.** The rest of the product is
 * square and shadow-raised; this screen is the soft, hairline-bordered, rounded treatment the
 * redesign was asked for, scoped under `.lq-ov` so no other screen changes. A shared `Card` with a
 * variant would have put an Overview-only decision into a kit component twenty screens import.
 *
 * **Every panel and tile is always rendered, and only its contents change state.** The entrance
 * timeline animates the panels as the page arrives; if a panel appeared only once its data did, the
 * choreography would depend on which read happened to answer first and the layout would jump as each
 * one landed. A skeleton holds the panel's real shape — a KPI skeleton is a label, a figure and a
 * foot line — so "the dashboard is loading naturally" is literally true: nothing moves when it fills.
 */

import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { HelpLabel } from '@/shared/ui/InfoTip';
import { EASE, useCountUp, useReveal } from '@/shared/ui/motion';

/** A section of the Overview: title, an optional one-line subtitle, optional controls on the right. */
export function Panel({
  title,
  help,
  subtitle,
  actions,
  busy = false,
  className,
  children,
}: {
  readonly title: string;
  /** What the panel's subject means, behind an "i" beside the title. */
  readonly help?: string | undefined;
  readonly subtitle?: ReactNode;
  readonly actions?: ReactNode;
  readonly busy?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <section
      className={className === undefined ? 'lq-ov-panel' : `lq-ov-panel ${className}`}
      data-ov="panel"
      aria-busy={busy}
    >
      <header className="lq-ov-panel__head">
        <div className="lq-ov-panel__titles">
          <HelpLabel subject={title} help={help}>
            <h2 className="lq-ov-panel__title">{title}</h2>
          </HelpLabel>
          {subtitle === undefined ? null : <p className="lq-ov-panel__sub">{subtitle}</p>}
        </div>
        {actions === undefined ? null : (
          <div className="lq-ov-panel__actions">{actions}</div>
        )}
      </header>
      {children}
    </section>
  );
}

type SkeletonLine = 'short' | 'medium' | 'long' | 'figure' | 'block';

/* Full class strings, so every name the page renders is spelled out where a reader can find it. */
const SKELETON_CLASS = {
  short: 'lq-ov-skel__bar lq-ov-skel__bar--short',
  medium: 'lq-ov-skel__bar lq-ov-skel__bar--medium',
  long: 'lq-ov-skel__bar lq-ov-skel__bar--long',
  figure: 'lq-ov-skel__bar lq-ov-skel__bar--figure',
  block: 'lq-ov-skel__bar lq-ov-skel__bar--block',
} as const satisfies Record<SkeletonLine, string>;

/** Grey bars in the shape of the content they replace, with an accessible name saying what is being
 * read. `role="status"` so the arrival of the real content is not a silent change. */
export function Skeleton({
  label,
  lines,
}: {
  readonly label: string;
  readonly lines: readonly SkeletonLine[];
}): ReactElement {
  return (
    <div className="lq-ov-skel" role="status" aria-label={label}>
      {lines.map((line, index) => (
        <span key={index} className={SKELETON_CLASS[line]} />
      ))}
    </div>
  );
}

/** The tones a KPI's icon chip can take. */
export type KpiTone = 'indigo' | 'blue' | 'mint' | 'amber' | 'coral' | 'neutral';

const ICON_CLASS = {
  indigo: 'lq-ov-kpi__icon lq-ov-kpi__icon--indigo',
  blue: 'lq-ov-kpi__icon lq-ov-kpi__icon--blue',
  mint: 'lq-ov-kpi__icon lq-ov-kpi__icon--mint',
  amber: 'lq-ov-kpi__icon lq-ov-kpi__icon--amber',
  coral: 'lq-ov-kpi__icon lq-ov-kpi__icon--coral',
  /* While the figure is being read or could not be: no state to colour, so no colour. */
  neutral: 'lq-ov-kpi__icon lq-ov-kpi__icon--neutral',
} as const satisfies Record<KpiTone, string>;

/** What a KPI tile shows in place of a figure. */
export type KpiReading =
  | { readonly kind: 'loading' }
  | { readonly kind: 'value'; readonly value: number }
  /* A word, never a zero: "not scored yet" and "could not be read" are different facts from a
     measured 0, and the reason sits on `title` for whoever needs it. */
  | { readonly kind: 'absent'; readonly word: string; readonly why?: string | undefined };

/** One headline figure: icon and label, the number, an optional picture of it, one foot line.
 *
 * **The number counts up once, on arrival, and is the real value in the DOM throughout** — see
 * `useCountUp`. Proportional figures rather than `tabular-nums`, which the data-viz guidance reserves
 * for columns: a lone `1,284` set in equal-width digits looks loose at display size.
 *
 * **The whole tile opens the screen behind the figure**, through the label rather than by making the
 * tile one link: the label is the link's accessible name — "Open incidents", not every word on the
 * tile read as one run-on sentence — and a pseudo-element stretches its target over the card. That is
 * also why nothing else in a tile is a link: a second link inside a stretched one is unreachable by
 * pointer, and nested links are not HTML.
 *
 * **The explanation's "i" sits beside the heading, never inside it**, so it is not part of the
 * link's name. It still takes the pointer above the stretched target: the trigger is positioned and
 * later in the DOM than the link's `::after`, so it paints — and is hit-tested — on top of it.
 */
export function Kpi({
  icon,
  tone,
  label,
  help,
  to,
  reading,
  format,
  unit,
  picture,
  foot,
}: {
  readonly icon: ReactNode;
  readonly tone: KpiTone;
  readonly label: string;
  /** What the figure means, behind an "i" beside the label. */
  readonly help?: string | undefined;
  readonly to: string;
  readonly reading: KpiReading;
  readonly format: (n: number) => string;
  readonly unit?: string | undefined;
  readonly picture?: ReactNode;
  readonly foot?: ReactNode;
}): ReactElement {
  const count = useCountUp(
    reading.kind === 'value' ? reading.value : null,
    format,
    'kpi',
  );

  return (
    <article className="lq-ov-kpi" data-ov="kpi" aria-busy={reading.kind === 'loading'}>
      <header className="lq-ov-kpi__top">
        <span className={ICON_CLASS[tone]}>{icon}</span>
        {/* `h2`, like a panel's title: each figure is a section of the page, and an `h3` here would
            skip a level beneath the page's `h1`. */}
        <HelpLabel subject={label} help={help}>
          <h2 className="lq-ov-kpi__label">
            <Link className="lq-ov-kpi__link" to={to}>
              {label}
            </Link>
          </h2>
        </HelpLabel>
      </header>
      {reading.kind === 'loading' ? (
        <Skeleton label={label} lines={['figure', 'medium']} />
      ) : (
        <>
          <p className="lq-ov-kpi__figure">
            {reading.kind === 'value' ? (
              <>
                <span ref={count} className="lq-ov-kpi__value">
                  {format(reading.value)}
                </span>
                {unit === undefined ? null : (
                  <span className="lq-ov-kpi__unit">{unit}</span>
                )}
              </>
            ) : (
              <span className="lq-ov-kpi__absent" title={reading.why}>
                {reading.word}
              </span>
            )}
          </p>
          {picture === undefined ? null : (
            <div className="lq-ov-kpi__picture">{picture}</div>
          )}
          {foot === undefined ? null : <div className="lq-ov-kpi__foot">{foot}</div>}
        </>
      )}
    </article>
  );
}

/** The fills a strip segment can take: the data mark, two status hues, and the quiet rest. */
export type StripTone = 'mark' | 'coral' | 'amber' | 'neutral';

const SEGMENT_CLASS = {
  mark: 'lq-ov-strip__seg lq-ov-strip__seg--mark',
  coral: 'lq-ov-strip__seg lq-ov-strip__seg--coral',
  amber: 'lq-ov-strip__seg lq-ov-strip__seg--amber',
  neutral: 'lq-ov-strip__seg lq-ov-strip__seg--neutral',
} as const satisfies Record<StripTone, string>;

export interface StripSegment {
  readonly id: string;
  /** This segment's part of the whole, 0..1. A zero share draws nothing rather than a sliver. */
  readonly share: number;
  readonly tone: StripTone;
}

/** A KPI's picture: one rounded bar split into its parts, with a 2px gap between them.
 *
 * **The words beside it carry the meaning; this restates it.** It is one image with one accessible
 * name, because six pixels of colour are not something a screen reader can usefully walk segment by
 * segment, and the quiet fills sit under 3:1 on purpose — the counts are written in the foot line.
 *
 * **`hatchRemainder` draws what is *not* covered as "no reading here"**, the stripes the product uses
 * for an unmeasured dimension. Only for a whole that the segments do not exhaust — datasets scored out
 * of datasets catalogued — and never for a severity split, whose bands are the whole by construction.
 */
export function Strip({
  segments,
  label,
  hatchRemainder = false,
}: {
  readonly segments: readonly StripSegment[];
  readonly label: string;
  readonly hatchRemainder?: boolean;
}): ReactElement {
  const drawn = segments.filter((segment) => segment.share > 0);
  const covered = drawn.reduce((sum, segment) => sum + segment.share, 0);
  const rest = Math.max(0, 1 - covered);

  const strip = useReveal<HTMLDivElement>(
    true,
    (root, timeline) => {
      if (root.children.length === 0) return;
      timeline.from(root.children, {
        scaleX: 0,
        transformOrigin: '0% 50%',
        duration: 0.7,
        ease: EASE.draw,
        stagger: 0.08,
      });
    },
    'kpi',
  );

  return (
    <div ref={strip} className="lq-ov-strip" role="img" aria-label={label}>
      {drawn.map((segment) => (
        <span
          key={segment.id}
          className={SEGMENT_CLASS[segment.tone]}
          style={{ flexGrow: segment.share }}
        />
      ))}
      {hatchRemainder && rest > 0.001 ? (
        <span className="lq-ov-strip__rest" style={{ flexGrow: rest }} />
      ) : null}
    </div>
  );
}
